import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { socket, type ConnStatus } from '../net/socket';
import type { ClientMessage, PublicState, ServerMessage } from '../../shared/protocol';

export interface EchoEntry {
  from: string;
  text: string;
  ts: number;
}
export interface ErrEntry {
  code: string;
  message: string;
  ts: number;
}

interface Ctx {
  status: ConnStatus;
  state: PublicState | null;
  echoes: EchoEntry[];
  lastError: ErrEntry | null;
  send: (m: ClientMessage) => void;
  /** v22 BOLT-108: a deliberate Create/Join from Home — clears any stale resume
   *  token so the auto-rejoin can't hijack it, and queues so a mid-connect
   *  socket can't drop it. Every explicit new-game action goes through here. */
  startFresh: (m: ClientMessage) => void;
  /** send an idempotent action that survives a reconnect (resent until acked) */
  sendReliable: (m: ClientMessage & { idem: string }) => void;
  /** subscribe to raw server messages (e.g. found_result, card_ack) */
  subscribe: (l: (m: ServerMessage) => void) => () => void;
  leave: () => void;
}

const SocketContext = createContext<Ctx | null>(null);

export function SocketProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ConnStatus>('closed');
  const [state, setState] = useState<PublicState | null>(null);
  const [echoes, setEchoes] = useState<EchoEntry[]>([]);
  const [lastError, setLastError] = useState<ErrEntry | null>(null);
  const extraListeners = useRef(new Set<(m: ServerMessage) => void>());
  // v12 §2B: once we LEAVE (or the host closes the lobby), in-flight state
  // broadcasts must not re-materialize the room — ignore state frames until
  // the next `session` handshake (a fresh create/join/rejoin) re-arms us.
  const left = useRef(false);

  useEffect(() => {
    const offStatus = socket.onStatus(setStatus);
    const off = socket.on((msg: ServerMessage) => {
      switch (msg.t) {
        case 'session':
          left.current = false;
          // v15 BOLT-062: entering a session retires any old Landing error —
          // a past game's complaint must never haunt the home screen.
          setLastError(null);
          break;
        case 'state':
          if (left.current) break;
          setState(msg.state);
          break;
        case 'echo':
          setEchoes((e) => [...e.slice(-19), { from: msg.from, text: msg.text, ts: Date.now() }]);
          break;
        case 'error':
          // v12 §2B: the host closed the lobby — forget the session and land
          // on Home (Landing renders once state is null). Every other error
          // stays a surfaced toast/banner.
          if (msg.code === 'session_closed') {
            left.current = true;
            socket.forget();
            setState(null);
            setEchoes([]);
            setLastError(null);
            break;
          }
          setLastError({ code: msg.code, message: msg.message, ts: Date.now() });
          break;
        case 'card_ack':
          socket.ack(msg.idem);
          break;
        case 'found_result':
          socket.ack(msg.idem);
          break;
        case 'hider_ack':
          socket.ack(msg.idem);
          break;
      }
      for (const l of extraListeners.current) l(msg);
    });
    socket.connect();
    return () => {
      offStatus();
      off();
    };
  }, []);

  const send = useCallback((m: ClientMessage) => socket.send(m), []);
  const startFresh = useCallback((m: ClientMessage) => {
    left.current = false; // a deliberate new game re-arms the state pipe
    socket.startFresh(m);
  }, []);
  const sendReliable = useCallback((m: ClientMessage & { idem: string }) => socket.sendReliable(m), []);
  const subscribe = useCallback((l: (m: ServerMessage) => void) => {
    extraListeners.current.add(l);
    return () => extraListeners.current.delete(l);
  }, []);
  const leave = useCallback(() => {
    left.current = true; // drop in-flight broadcasts (re-armed by the next `session`)
    socket.forget();
    setState(null);
    setEchoes([]);
    setLastError(null);
  }, []);

  const value = useMemo<Ctx>(
    () => ({ status, state, echoes, lastError, send, startFresh, sendReliable, subscribe, leave }),
    [status, state, echoes, lastError, send, startFresh, sendReliable, subscribe, leave],
  );
  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useGame(): Ctx {
  const ctx = useContext(SocketContext);
  if (!ctx) throw new Error('useGame must be used within SocketProvider');
  return ctx;
}
